import { useCallback, useEffect, useRef, useState } from "react";

const TIMER_MODES = [10, 20, 5, 0];
const FADE_MODES = [30, 45, 60, 0];

function fmtT(s) {
  s = Math.max(0, Math.ceil(s));
  return Math.floor(s / 60) + ":" + String(Math.floor(s % 60)).padStart(2, "0");
}

export function useSleepControls({ playing, ready, setVolume, pause, asleep, setAsleep, track }) {
  const [timerIdx, setTimerIdx] = useState(0);
  const [fadeIdx, setFadeIdx] = useState(0);
  const playSecRef = useRef(0);
  const fadeSecRef = useRef(0);
  const autoDimmedRef = useRef(false);
  const fadeDoneRef = useRef(false);
  const [timerLabel, setTimerLabel] = useState("AUTO 10:00");
  const [fadeLabel, setFadeLabel] = useState("FADE 30:00");

  useEffect(() => {
    const lim = TIMER_MODES[timerIdx] * 60;
    setTimerLabel(lim ? "AUTO " + fmtT(lim - playSecRef.current) : "AUTO OFF");
  }, [timerIdx]);

  useEffect(() => {
    const lim = FADE_MODES[fadeIdx] * 60;
    setFadeLabel(lim ? "FADE " + fmtT(lim - fadeSecRef.current) : "FADE OFF");
  }, [fadeIdx]);

  useEffect(() => {
    if (!playing || !ready) return;
    const interval = setInterval(() => {
      playSecRef.current += 0.5;
      const lim = TIMER_MODES[timerIdx] * 60;
      if (lim) setTimerLabel("AUTO " + fmtT(lim - playSecRef.current));
      if (lim && playSecRef.current >= lim && !autoDimmedRef.current) {
        autoDimmedRef.current = true;
        if (!asleep) { setAsleep(true); track("auto_lights_off"); }
      }
      const flim = FADE_MODES[fadeIdx] * 60;
      if (flim) {
        fadeSecRef.current += 0.5;
        setFadeLabel("FADE " + fmtT(flim - fadeSecRef.current));
        const halfway = flim / 2;
        let vol = 100;
        if (fadeSecRef.current > halfway) vol = Math.max(0, (100 * (flim - fadeSecRef.current)) / (flim - halfway));
        setVolume(vol);
        if (fadeSecRef.current >= flim && !fadeDoneRef.current) {
          fadeDoneRef.current = true;
          pause();
          if (!asleep) setAsleep(true);
          track("sleep_fade_complete", { min: FADE_MODES[fadeIdx] });
          fadeSecRef.current = 0;
          setTimeout(() => { setVolume(100); fadeDoneRef.current = false; }, 250);
        }
      }
    }, 500);
    return () => clearInterval(interval);
  }, [playing, ready, asleep, timerIdx, fadeIdx, setAsleep, setVolume, pause, track]);

  const onTimerClick = useCallback(() => {
    setTimerIdx((i) => {
      const next = (i + 1) % TIMER_MODES.length;
      track("timer_mode", { min: TIMER_MODES[next] });
      return next;
    });
    playSecRef.current = 0;
    autoDimmedRef.current = false;
  }, [track]);

  const onFadeClick = useCallback(() => {
    setFadeIdx((i) => {
      const next = (i + 1) % FADE_MODES.length;
      track("fade_mode", { min: FADE_MODES[next] });
      return next;
    });
    fadeSecRef.current = 0;
    fadeDoneRef.current = false;
    setVolume(100);
  }, [setVolume, track]);

  return { timerLabel, fadeLabel, onTimerClick, onFadeClick };
}
